import React, { useState } from 'react';

import { PlanData, PlanSkill, SectionConfig } from './types';

// Flat taxonomy (ACT): every skill is its own domain. 2-level (SAT/SHSAT):
// skills roll up under distinct domains. Drives whether the accuracy-gap views
// label by section (flat) or by domain (2-level).
const isFlatTaxonomy = (data: PlanData): boolean =>
    data.skills.length > 0 && data.skills.every(s => s.skill === s.domain);

// All / per-section filter chips driven by the plan's section config.
function SectionChips({ sections, value, onChange }: {
    sections: SectionConfig[]; value: string; onChange: (v: string) => void;
}) {
    const opts: [string, string][] = [['all', 'All'], ...sections.map(s => [s.key, s.label] as [string, string])];
    return (
        <div className='r-chips'>
            {opts.map(([k, l]) => (
                <button key={k} className={value === k ? 'r-chip r-chip--on' : 'r-chip'} onClick={() => onChange(k)}>{l}</button>
            ))}
        </div>
    );
}

// ── Scaled-points (SAT + ACT) — Domain Proficiency: earned (green) + on-table (striped) ──
// SAT: bars are 2-level domains, all feed the composite (a sum), points are
// section-scale = composite-scale. ACT: bars are flat categories, points are
// section-scale (toward the 1-36 section); Science feeds STEM only and is
// badged "not in Composite".
function DomainProficiency({ data }: { data: PlanData }) {
    const rollup = data.domainRollup;
    if (!rollup.length) return null;
    const maxPts = Math.max(1, ...rollup.map(r => r.ptsMax ?? 0));
    const flat = isFlatTaxonomy(data);
    const unit = flat ? 'category' : 'domain';

    return (
        <div className='r-card'>
            <div className='r-card__head'><div>
                <div className='r-card__title'>Domain Proficiency</div>
                <div className='r-card__sub'>
                    Each bar is one {unit}'s score contribution: the points the student
                    already earns (green) and the points still on the table at their current accuracy
                    (striped). Longer bars = {unit === 'category' ? 'categories' : 'domains'} worth more points per test.
                </div>
            </div></div>
            <div className='r-grid2' style={{ gap: 24 }}>
                {data.meta.sections.map(sec => {
                    const doms = rollup.filter(d => d.section === sec.key).slice().sort((a, b) => (b.pts ?? 0) - (a.pts ?? 0));
                    if (!doms.length) return <div key={sec.key} />;
                    const inComposite = data.meta.compositeKeys.includes(sec.key);
                    return (
                        <div key={sec.key}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: sec.color }}>
                                <span className='r-dot' style={{ background: sec.color }} />
                                {sec.label}
                                {!inComposite && (
                                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', color: '#828282', background: '#F1F5F9', border: '1px solid #EAECF0', borderRadius: 10, padding: '1px 7px', textTransform: 'none' }}>
                                        STEM only · not in Composite
                                    </span>
                                )}
                            </div>
                            {doms.map(d => {
                                const ptsMax = d.ptsMax ?? 0;
                                const pts = d.pts ?? 0;
                                const earned = ptsMax - pts;
                                const earnedW = (earned / maxPts) * 100;
                                const upsideW = (pts / maxPts) * 100;
                                return (
                                    <div key={d.domain} style={{ padding: '7px 2px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2434', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.domain}</div>
                                                <div style={{ fontSize: 10, color: '#828282' }}>{d.q} {d.q === 1 ? 'q' : 'qs'}/test · {d.acc}% accuracy</div>
                                            </div>
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <div style={{ fontSize: 14, fontWeight: 700, color: d.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>+{Math.round(pts)}</div>
                                                <div style={{ fontSize: 9, color: '#828282', marginTop: 1 }}>/+{Math.round(ptsMax)} max</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', height: 18, borderRadius: 4, overflow: 'hidden', background: '#F1F5F9', border: '1px solid #EAECF0' }}>
                                            <div style={{ width: `${earnedW}%`, background: '#bfe5cc', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 5, fontSize: 9, fontWeight: 700, color: '#005c20' }}>
                                                {earnedW > 12 ? Math.round(earned) : ''}
                                            </div>
                                            <div style={{ width: `${upsideW}%`, background: `repeating-linear-gradient(45deg, ${d.color}, ${d.color} 4px, ${d.color}cc 4px, ${d.color}cc 8px)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff' }}>
                                                {upsideW > 8 ? `+${Math.round(pts)}` : ''}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#F9FAFB', border: '1px solid #EAECF0', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 18, fontSize: 12, alignItems: 'center', flexWrap: 'wrap', color: '#505050' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 14, height: 12, background: '#bfe5cc', borderRadius: 2 }} />
                        Points the student already earns
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 14, height: 12, borderRadius: 2, background: 'repeating-linear-gradient(45deg, #0575a2, #0575a2 3px, #0575a2cc 3px, #0575a2cc 6px)' }} />
                        Points still on the table
                    </span>
                </div>
                <div style={{ fontSize: 11, color: '#828282', lineHeight: 1.5 }}>
                    Points are estimated from the student's accuracy so far: % wrong × questions per
                    test × points per question at their current score range.
                </div>
            </div>
        </div>
    );
}

// Top Improvement Targets show the top 8 by default; the rest collapse behind
// a Show more toggle.
const TARGETS_LIMIT = 8;

function ShowMoreTargets({ total, showAll, onToggle }: { total: number; showAll: boolean; onToggle: () => void }) {
    if (total <= TARGETS_LIMIT) return null;
    return (
        <div className='r-more'>
            <button type='button' className={showAll ? 'r-more__btn r-more__btn--open' : 'r-more__btn'} onClick={onToggle}>
                {showAll ? 'Show less' : `Show ${total - TARGETS_LIMIT} more`}
                <svg width='11' height='11' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5' strokeLinecap='round' strokeLinejoin='round'>
                    <polyline points='6 9 12 15 18 9' />
                </svg>
            </button>
        </div>
    );
}

// ── Scaled-points (SAT + ACT) — Top Improvement Targets: ranked by upside ──
// 2-level (SAT): rows are leaf skills, sub-labelled by their domain. Flat (ACT):
// rows are categories, sub-labelled by section; Science rows are badged and
// their points count toward the section (STEM), not the composite.
function PrioritySkills({ data }: { data: PlanData }) {
    const [filter, setFilter] = useState<string>('all');
    const [showAll, setShowAll] = useState(false);
    const rows = data.skills.filter(s => filter === 'all' || s.section === filter);
    const visible = showAll ? rows : rows.slice(0, TARGETS_LIMIT);
    const flat = isFlatTaxonomy(data);
    const sectionLabel = (key: string) => data.meta.sections.find(s => s.key === key)?.label || key;

    return (
        <div className='r-card'>
            <div className='r-card__head'>
                <div>
                    <div className='r-card__title'>Top Improvement Targets</div>
                    <div className='r-card__sub'>
                        The detailed {flat ? 'categories' : 'topics'} where focused practice buys the most points — ranked by
                        % wrong × questions per test × points per question. A high-volume {flat ? 'category' : 'skill'} at
                        moderate accuracy outranks a rare one the student fails outright.
                    </div>
                </div>
                <SectionChips sections={data.meta.sections} value={filter} onChange={v => { setFilter(v); setShowAll(false); }} />
            </div>
            <div className='r-colhead'><div /><div>{flat ? 'Category' : 'Skill'}</div><div>Accuracy</div><div>Q/test</div><div>Upside</div></div>
            {visible.map((s: PlanSkill, i: number) => {
                const inComposite = data.meta.compositeKeys.includes(s.section);
                return (
                <div className='r-skill' key={s.skill}>
                    <div className='r-skill__rank'>{i + 1}</div>
                    <div style={{ minWidth: 0 }}>
                        <div className='r-skill__name'>{s.skill}</div>
                        <div className='r-skill__dom'>
                            <span className='r-dot' style={{ background: s.color }} />{flat ? sectionLabel(s.section) : s.domain}
                            {!inComposite && (
                                <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: '0.03em', color: '#828282', background: '#F1F5F9', border: '1px solid #EAECF0', borderRadius: 10, padding: '0 6px' }}>
                                    STEM only
                                </span>
                            )}
                        </div>
                    </div>
                    <div className='r-acc'>
                        <div className='r-acc__track'><div className='r-acc__fill' style={{ width: `${s.pct}%`, background: s.color }} /></div>
                        <div className='r-acc__pct'>{Math.round(s.pct)}%</div>
                    </div>
                    <div className='r-skill__q'>{s.qPerTest}</div>
                    <div className='r-skill__up'>
                        <div className='r-skill__upn'>+{Math.round(s.ptUpsideExpected ?? 0)}</div>
                        <div className='r-skill__upl'>of +{Math.round(s.ptUpsideMax ?? 0)} max</div>
                    </div>
                </div>
                );
            })}
            {!rows.length && <div className='r-emptycard'>No {flat ? 'categories' : 'skills'} assessed yet.</div>}
            <ShowMoreTargets total={rows.length} showAll={showAll} onToggle={() => setShowAll(v => !v)} />
        </div>
    );
}

// ── Accuracy-gap — Domain Proficiency: accuracy bars by section ──
// One bar per reporting unit: a flat category (ACT) or a rolled-up domain
// (SHSAT, its skills combined). Bar length ∝ questions per test (how much it
// matters); the solid part is what the student already answers correctly, the
// striped part is the questions they're missing at their current accuracy.
function CategoryProficiency({ data }: { data: PlanData }) {
    const rows = data.domainRollup;
    if (!rows.length) return null;
    const maxQ = Math.max(1, ...rows.map(r => r.q));
    const flat = isFlatTaxonomy(data);

    return (
        <div className='r-card'>
            <div className='r-card__head'><div>
                <div className='r-card__title'>Domain Proficiency</div>
                <div className='r-card__sub'>
                    Each bar is one reporting {flat ? 'category' : 'domain (its skills combined)'}. Bar
                    length reflects how many questions it's worth per test; the solid part is what the
                    student already answers correctly and the striped part is the questions they're
                    missing at their current accuracy.
                </div>
            </div></div>
            <div className='r-grid2' style={{ gap: 24 }}>
                {data.meta.sections.map(sec => {
                    const cats = rows.filter(r => r.section === sec.key).slice().sort((a, b) => (b.qMissed ?? 0) - (a.qMissed ?? 0));
                    if (!cats.length) return <div key={sec.key} />;
                    const inComposite = data.meta.compositeKeys.includes(sec.key);
                    return (
                        <div key={sec.key}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: sec.color }}>
                                <span className='r-dot' style={{ background: sec.color }} />
                                {sec.label}
                                {!inComposite && (
                                    <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', color: '#828282', background: '#F1F5F9', border: '1px solid #EAECF0', borderRadius: 10, padding: '1px 7px', textTransform: 'none' }}>
                                        STEM only · not in Composite
                                    </span>
                                )}
                            </div>
                            {cats.map(c => {
                                const barW = Math.max(18, (c.q / maxQ) * 100);
                                const qMissed = c.qMissed ?? 0;
                                return (
                                    <div key={c.domain} style={{ padding: '7px 2px' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                                            <div style={{ minWidth: 0, flex: 1 }}>
                                                <div style={{ fontSize: 13, fontWeight: 600, color: '#1C2434', lineHeight: 1.25, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.domain}</div>
                                                <div style={{ fontSize: 10, color: '#828282' }}>{c.q} {c.q === 1 ? 'q' : 'qs'}/test · {c.acc}% accuracy</div>
                                            </div>
                                            <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                                <div style={{ fontSize: 14, fontWeight: 700, color: c.color, fontVariantNumeric: 'tabular-nums', lineHeight: 1 }}>~{qMissed.toFixed(1)}</div>
                                                <div style={{ fontSize: 9, color: '#828282', marginTop: 1 }}>q/test missed</div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', height: 18, width: `${barW}%`, minWidth: 60, borderRadius: 4, overflow: 'hidden', background: '#F1F5F9', border: '1px solid #EAECF0' }}>
                                            <div style={{ width: `${c.acc}%`, background: '#bfe5cc', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: 5, fontSize: 9, fontWeight: 700, color: '#005c20' }}>
                                                {c.acc > 18 ? `${c.acc}%` : ''}
                                            </div>
                                            <div style={{ width: `${100 - c.acc}%`, background: `repeating-linear-gradient(45deg, ${c.color}, ${c.color} 4px, ${c.color}cc 4px, ${c.color}cc 8px)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#fff' }}>
                                                {100 - c.acc > 12 ? `${100 - c.acc}%` : ''}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    );
                })}
            </div>
            <div style={{ marginTop: 14, padding: '10px 14px', background: '#F9FAFB', border: '1px solid #EAECF0', borderRadius: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                <div style={{ display: 'flex', gap: 18, fontSize: 12, alignItems: 'center', flexWrap: 'wrap', color: '#505050' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 14, height: 12, background: '#bfe5cc', borderRadius: 2 }} />
                        Answered correctly
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 14, height: 12, borderRadius: 2, background: 'repeating-linear-gradient(45deg, #a23205, #a23205 3px, #a23205cc 3px, #a23205cc 6px)' }} />
                        Questions missed (winnable)
                    </span>
                </div>
                <div style={{ fontSize: 11, color: '#828282', lineHeight: 1.5 }}>
                    Bar length is questions per test; the striped part is how many the student misses at
                    their current accuracy (questions per test × % wrong).
                </div>
            </div>
        </div>
    );
}

// ── Accuracy-gap — Top Improvement Targets: ranked by questions winnable ──
// Flat (ACT): rows are categories, sub-labelled by section. 2-level (SHSAT):
// rows are leaf skills, sub-labelled by their domain.
function PriorityCategories({ data }: { data: PlanData }) {
    const [filter, setFilter] = useState<string>('all');
    const [showAll, setShowAll] = useState(false);
    const rows = data.skills.filter(s => filter === 'all' || s.section === filter);
    const visible = showAll ? rows : rows.slice(0, TARGETS_LIMIT);
    const sectionLabel = (key: string) => data.meta.sections.find(s => s.key === key)?.label || key;
    const flat = isFlatTaxonomy(data);
    const unit = flat ? 'reporting categories' : 'skills';

    return (
        <div className='r-card'>
            <div className='r-card__head'>
                <div>
                    <div className='r-card__title'>Top Improvement Targets</div>
                    <div className='r-card__sub'>
                        The {unit} where focused practice wins back the most questions —
                        ranked by questions missed per test (questions per test × % wrong). A high-volume
                        {flat ? ' category' : ' skill'} at moderate accuracy outranks a rare one the student fails outright.
                    </div>
                </div>
                <SectionChips sections={data.meta.sections} value={filter} onChange={v => { setFilter(v); setShowAll(false); }} />
            </div>
            <div className='r-colhead'><div /><div>{flat ? 'Category' : 'Skill'}</div><div>Accuracy</div><div>Q/test</div><div>Winnable</div></div>
            {visible.map((s: PlanSkill, i: number) => {
                const inComposite = data.meta.compositeKeys.includes(s.section);
                return (
                    <div className='r-skill' key={s.skill}>
                        <div className='r-skill__rank'>{i + 1}</div>
                        <div style={{ minWidth: 0 }}>
                            <div className='r-skill__name'>{s.skill}</div>
                            <div className='r-skill__dom'>
                                <span className='r-dot' style={{ background: s.color }} />{flat ? sectionLabel(s.section) : s.domain}
                                {!inComposite && (
                                    <span style={{ marginLeft: 6, fontSize: 9, fontWeight: 700, letterSpacing: '0.03em', color: '#828282', background: '#F1F5F9', border: '1px solid #EAECF0', borderRadius: 10, padding: '0 6px' }}>
                                        STEM only
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className='r-acc'>
                            <div className='r-acc__track'><div className='r-acc__fill' style={{ width: `${s.pct}%`, background: s.color }} /></div>
                            <div className='r-acc__pct'>{Math.round(s.pct)}%</div>
                        </div>
                        <div className='r-skill__q'>{s.qPerTest}</div>
                        <div className='r-skill__up'>
                            <div className='r-skill__upn'>~{(s.qMissedPerTest ?? 0).toFixed(1)}</div>
                            <div className='r-skill__upl'>q/test</div>
                        </div>
                    </div>
                );
            })}
            {!rows.length && <div className='r-emptycard'>No {flat ? 'categories' : 'skills'} assessed yet.</div>}
            <ShowMoreTargets total={rows.length} showAll={showAll} onToggle={() => setShowAll(v => !v)} />
        </div>
    );
}

function FocusAreas({ data }: { data: PlanData }) {
    if (data.meta.leverageMode === 'accuracy-gap') {
        return (
            <div className='r-wrap' style={{ paddingTop: 0, gap: 18 }}>
                <CategoryProficiency data={data} />
                <PriorityCategories data={data} />
            </div>
        );
    }
    return (
        <div className='r-wrap' style={{ paddingTop: 0, gap: 18 }}>
            <DomainProficiency data={data} />
            <PrioritySkills data={data} />
        </div>
    );
}

export default FocusAreas;
